import _ from 'underscore';

import { ProjectMapper } from '~src/projects/models/mappers/ProjectMapper';
import { IPublishedConnectionUsageInfo } from '~src/spacesPlatform/models/IUsageInfo';
import { ContentMapper } from '~src/spacesPlatform/models/mappers/ContentMapper';
import { MapperHelper } from '~src/spacesPlatform/models/mappers/MapperHelper';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';
import { UserMapper } from '~src/userManagement/users/models/mappers/UserMapper';

import { IPublishedConnection, IPublishedConnectionDetail } from '../../serverApi/types/IPublishedConnection';
import {
  IPublishedConnectionDetailInfo,
  IPublishedConnectionDraftInfo,
  IPublishedConnectionInfo
} from '../IPublishedConnectionInfo';

export module PublishedConnectionMapper {
  const M = MapperHelper;

  export function map(
    serverApiMetrics: IPublishedConnection[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo>,
    favoritePublishedConnections: _.Dictionary<any> = {}
  ): IPublishedConnectionInfo[] {
    const vConnsToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.virtualConnection, projects);
    return serverApiMetrics.map((m: IPublishedConnection) => {
      return mapOne(m, users[m.ownerId], vConnsToProject[m.id], !!favoritePublishedConnections[m.id]);
    });
  }

  export function mapOne(
    publishedConnection: IPublishedConnection,
    owner?: IUserInfo,
    project?: INamedItemInfo,
    favorite?: boolean
  ): IPublishedConnectionInfo {
    return {
      createdAt: M.toDate(publishedConnection.createdAt),
      updatedAt: M.toDate(publishedConnection.updatedAt),
      description: publishedConnection.description,
      id: publishedConnection.id,
      luid: publishedConnection.luid,
      name: publishedConnection.name,
      owner: UserMapper.mapOne(owner),
      favorite: favorite,
      contentType: 'virtualConnection',
      tags: M.sortTags(publishedConnection.tags),
      project: project,
      // Threaded from the REST `connectionType` upstream so EAD PFT events for
      // virtualConnection content surface a populated `connection_type` Custom
      // Attribute (W-22931576). Stays undefined when the server omits the
      // field -- which matches the existing `?? ''` empty-string sentinel at
      // the emit sites in the three EAD telemetry helpers.
      catalogConnectionType: publishedConnection.connectionType,
      dataQualityIndicators:  publishedConnection.dataQualityIndicators || [],
      hasExtracts: publishedConnection.hasExtract,
      lastRefreshedAt: publishedConnection.hasExtract ? M.toDate(publishedConnection.extractsRefreshedAt) : undefined,
      usageInfo: <IPublishedConnectionUsageInfo>{favoritesTotal: 0},
      usageInfoMaxValues: <IPublishedConnectionUsageInfo>{},
      draftStatus: publishedConnection.draftStatus,
      draftLuid: publishedConnection.draftLuid
    };
  }

  export function mapDetail(p: IPublishedConnectionDetail): IPublishedConnectionDetailInfo {
    const publishedConnectionDraft: IPublishedConnectionDraftInfo | undefined = p.publishedConnectionDraft ? {
        luid: p.publishedConnectionDraft.luid,
        ownerId: p.publishedConnectionDraft.ownerId,
        createdAt: p.publishedConnectionDraft.createdAt,
        updatedAt: p.publishedConnectionDraft.updatedAt,
      } : undefined;

    return {
      createdAt: M.toDate(p.createdAt),
      updatedAt: M.toDate(p.updatedAt),
      description: p.description,
      id: p.id,
      luid: p.luid,
      name: p.name,
      owner: UserMapper.mapOne(p.owner),
      favorite: p.favorite,
      contentType: 'virtualConnection',
      tags: M.sortTags(p.tags),
      project: ProjectMapper.mapName(p.project),
      tables: p.tables,
      catalogConnectionType: p.connectionType,
      dataQualityIndicators:  p.dataQualityIndicators || [],
      hasExtracts: p.hasExtract,
      lastRefreshedAt: p.hasExtract ? M.toDate(p.extractsRefreshedAt) : undefined,
      publishedConnectionDraft
    };
  }

  export function mapFromCRASS(p: any): IPublishedConnectionInfo {
    return {
      createdAt: M.toDate(p.createdTime) || M.toDateFromTimeOffsetObject(p.createdTime),
      updatedAt: M.toDate(p.modifiedTime) || M.toDateFromTimeOffsetObject(p.modifiedTime),
      description: p.description,
      id: p.id + '',
      luid: p.luid,
      name: p.title,
      favorite: false,
      contentType: 'virtualConnection',
      tags: M.sortTags(p.tags),
      project: {
        id: p.projectId ?? p.parentProjectId,
        name: p.containerName
      },
      owner: UserMapper.mapOneOwnerFromCRASS(p),
      // CRASS payloads aren't statically typed (the parameter is `any`), so
      // we read connectionType defensively; undefined when absent matches
      // the upstream-omitted case used by the other two mappers.
      catalogConnectionType: typeof p.connectionType === 'string' ? p.connectionType : undefined,
      dataQualityIndicators:  p.dataQualityIndicators || [],
      hasExtracts: p.hasExtract,
      lastRefreshedAt: p.hasExtract ? M.toDate(p.extractsRefreshedAt) : undefined,
      usageInfo: <IPublishedConnectionUsageInfo>{favoritesTotal: 0},
      usageInfoMaxValues: <IPublishedConnectionUsageInfo>{},
      draftStatus: p.draftStatus,
      draftLuid: p.draftLuid
    };
  }
}
